import type { QueryClient } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'

import { orgQueries } from '@/features/orgs/queries'
import { forgetCachedResponses } from '@/features/pwa/api-cache'
import {
  onSuspended,
  orgScope,
  orgStore,
  PLATFORM_SCOPE,
  refreshSession,
  type Schemas,
  type Scope,
  scopeStore,
  tokenStore,
} from '@/lib/api/client'
import { tokenExpiresAt } from '@/lib/api/token'

import type { EndedReason } from './login-search'
import { openedOrgs } from './opened-orgs'
import { authQueries } from './queries'

/**
 * The session's rules (docs/ARCHITECTURE.md): whether a visitor is signed in, which org they work in,
 * where they go after signing in, and signing out. The token and scope stores themselves are in
 * `lib/api/client.ts`, beside the middleware that sends them.
 */

export type Membership = Schemas['MembershipRead']
export type Me = Schemas['MeRead']

/**
 * Why a session ended: the member asked (`user`), or it ended without them (`expired`, `idle`,
 * `suspended`).
 */
export type SignOutReason = 'user' | EndedReason

export function hasValidToken(now = Date.now()): boolean {
  const token = tokenStore.get()
  if (!token) return false
  const expiresAt = tokenExpiresAt(token)
  return expiresAt === null || expiresAt > now
}

/**
 * Is there a session to go on with? Asked before anyone is sent to sign in.
 *
 * The access token is the short half and is expected to be stale: after fifteen minutes in another
 * tab, after a night with the laptop shut. The long half is the httpOnly cookie, and the only way
 * to find out whether it is still good is to spend it, so a missing or expired token is a question
 * for the API rather than a sign-out.
 *
 * Offline the API cannot be asked, so a token held at all, stale or not, goes on: the app opens on
 * what this device last loaded (features/pwa), and the API decides once the network is back.
 */
export async function hasSession(): Promise<boolean> {
  if (hasValidToken() || (await refreshSession())) return true
  return !navigator.onLine && tokenStore.get() !== null
}

/**
 * Where a signed-in user works (the spec, "The dashboard"): the scope they were last in while it
 * is still theirs (the platform for an operator, an org they still belong to, or one the operator
 * opened in this browser, so a reload does not end the visit), else the platform for an operator
 * who belongs to no org, else their first org. `null` is nowhere: a user who is neither an
 * operator nor a member of any org. A suspended org is not one a member can work in, so it is
 * passed over; the operator may still open it.
 */
export function resolveScope(
  me: Me,
  last: Scope | null,
  opened: readonly string[] = [],
): Scope | null {
  const operator = me.user.is_operator
  const workable = me.memberships.filter((m) => operator || !m.suspended)
  if (last?.kind === 'platform' && operator) return PLATFORM_SCOPE
  if (last?.kind === 'org' && workable.some((m) => m.org_id === last.orgId)) return last
  if (last?.kind === 'org' && operator && opened.includes(last.orgId)) return last
  const first = workable[0]
  if (first !== undefined) return orgScope(first.org_id)
  return operator ? PLATFORM_SCOPE : null
}

/**
 * Work in `orgId` from now on. The org is loaded first, naming itself, so nothing drawn for the
 * new scope waits on it. The platform operator may open any org (the platform console's spec,
 * "Opening an org"); one they do not belong to joins the orgs they opened recently.
 */
export async function enterOrg(queryClient: QueryClient, orgId: string): Promise<void> {
  const org = await queryClient.ensureQueryData(orgQueries.current(orgId))
  if (org.as_operator) {
    const me = await queryClient.ensureQueryData(authQueries.me())
    openedOrgs.remember(me.user.id, { id: org.id, name: org.name })
  }
  scopeStore.set(orgScope(orgId))
}

/** Work across the platform from now on: the operator's console (ADR 0003). */
export function enterPlatform(): void {
  scopeStore.set(PLATFORM_SCOPE)
}

/** Where to go after signing in: an in-app path only, so the link cannot send anyone elsewhere. */
export function safeRedirect(target: string | undefined): string {
  if (!target || !target.startsWith('/') || target.startsWith('//')) return '/'
  if (target.startsWith('/login')) return '/'
  return target
}

// Why the token went decides what the sign-in screen says. Any new token resets it.
let signOutReason: SignOutReason = 'expired'
tokenStore.subscribe(() => {
  if (tokenStore.get() !== null) signOutReason = 'expired'
})
// A refresh or a request refused because the org is suspended (`lib/api/client.ts`).
onSuspended(() => {
  signOutReason = 'suspended'
})

export function lastSignOutReason(): SignOutReason {
  return signOutReason
}

/**
 * End the session on this device: the token, the scope and every cached row go, the service
 * worker's copies included. Telling the API is
 * the logout mutation's job (`mutations.ts`); this is what happens whether or not it heard.
 */
export function signOut(queryClient: QueryClient, reason: SignOutReason): void {
  signOutReason = reason
  tokenStore.set(null)
  scopeStore.set(null)
  queryClient.clear()
  forgetCachedResponses()
}

export function useAccessToken(): string | null {
  return useSyncExternalStore(tokenStore.subscribe, tokenStore.get)
}

export function useOrgId(): string | null {
  return useSyncExternalStore(orgStore.subscribe, orgStore.get)
}

/** The active scope: the platform, or the org being worked in (`lib/api/client.ts`). */
export function useScope(): Scope | null {
  return useSyncExternalStore(scopeStore.subscribe, scopeStore.get)
}
